// Part of every editor, and deliberately free of the attachment node: this is how the static
// bundle asks for files to be taken in without importing the code that does it. The handlers are
// registered by `AttachmentsExtension`, which loads on demand; with no handler, a command is
// simply not handled, and the caller says the editor does not offer attachments.
import { createCommand, type LexicalCommand, type NodeKey } from "lexical";
import type { BlockId } from "../../core/types";

export type AttachmentRefusal =
  /** The editor was given no adapter: it shows attachments and takes none in. */
  | "not-offered"
  | "read-only"
  /** Every file was over `maxAttachmentBytes`. */
  | "too-large"
  /** Every file was of a kind the host does not accept. */
  | "unsupported-type";

/**
 * A command only answers "handled or not", so what became of the files comes back through the
 * payload. `accepted` is how many were handed to the adapter for staging: they are not in the
 * document yet, and each is put there, or reported, when the adapter answers.
 */
export interface AttachmentOutcome {
  refused: AttachmentRefusal | null;
  accepted: number;
}

export interface AddAttachmentsPayload {
  files: readonly File[];
  outcome: AttachmentOutcome;
}

/** Takes files in at the selection, as a drop or a paste would. */
export const ADD_ATTACHMENTS_COMMAND: LexicalCommand<AddAttachmentsPayload> = createCommand(
  "z-editor/add-attachments",
);

export interface PickAttachmentsPayload {
  /** `image` narrows the file dialog to images; what is picked is taken in either way. */
  kind: "image" | "file";
  outcome: AttachmentOutcome;
}

/**
 * Opens the platform's file dialog and takes in what is picked. It has to be dispatched from
 * inside the event of a key or a pointer: browsers open a file dialog for nothing else.
 */
export const PICK_ATTACHMENTS_COMMAND: LexicalCommand<PickAttachmentsPayload> = createCommand(
  "z-editor/pick-attachments",
);

export interface SetAttachmentAltPayload {
  /** Which attachment: by key from inside the editor, by block ID from a host. */
  target: { nodeKey: NodeKey } | { blockId: BlockId };
  alt: string;
  outcome: { refused: "unknown-block" | "invalid-target" | null };
}

/**
 * Writes an image's description. One undo step, and the only thing about an attachment that is
 * ever edited: everything else in its reference is what the host's store said when it kept it.
 */
export const SET_ATTACHMENT_ALT_COMMAND: LexicalCommand<SetAttachmentAltPayload> = createCommand(
  "z-editor/set-attachment-alt",
);

/** Why a file that was offered is not in the document. */
export type AttachmentRejectionReason =
  | "too-large"
  | "unsupported-type"
  /** The host's store refused it, or failed: its `StageRefusal`. */
  | "quota"
  | "failed"
  /** The host's store answered with a reference the document format does not accept. */
  | "invalid-attachment"
  /**
   * The editor that took the file was gone, or no longer for writing in, by the time it was
   * staged. It is in the host's store and in no document; an orphan sweep will find it.
   */
  | "editor-gone";

export interface AttachmentRejection {
  name: string;
  size: number;
  reason: AttachmentRejectionReason;
  /** The limit that was passed, for `too-large`. */
  max?: number;
  /** What the host's store said, where it said anything. */
  message?: string;
  /** The asset that was staged and then not used, for `editor-gone`. */
  assetId?: string;
}
